import type { LucideIcon } from "lucide-react";
import { tabBar } from "@web/theme/theme";

export type TabItem<K extends string = string> = { key: K; label: string; icon: LucideIcon; badge?: number };

export type TabBarProps<K extends string> = {
  items: readonly TabItem<K>[];
  active: K;
  onSelect: (key: K) => void;
  className?: string;
};

/** Bottom tab bar: sticky, safe-area bottom padding, ≥44px targets, accent for the
 * active tab. A tab may carry a count, which is announced in its label so a
 * screen reader hears "Today, 4 waiting" rather than a bare number. */
export function TabBar<K extends string>({ items, active, onSelect, className = "" }: TabBarProps<K>) {
  return (
    <nav aria-label="Main" className={`${tabBar.base} ${className}`.trim()}>
      <div role="tablist" className={tabBar.inner}>
        {items.map(({ key, label, icon: Icon, badge }) => {
          const isActive = key === active;
          return (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={isActive}
              aria-current={isActive ? "page" : undefined}
              onClick={() => onSelect(key)}
              aria-label={badge ? `${label}, ${badge} waiting` : undefined}
              className={`${tabBar.item} ${isActive ? tabBar.active : tabBar.inactive}`}
            >
              <span className={tabBar.icon}>
                <Icon aria-hidden size={24} strokeWidth={isActive ? 2.25 : 1.75} />
                {Boolean(badge) && <span className={tabBar.badge}>{badge! > 99 ? "99+" : badge}</span>}
              </span>
              <span>{label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
